<script setup lang="ts">
import { MoreHorizontalIcon } from '@blpde/nx-icons';
import {
  computed,
  markRaw,
  nextTick,
  onBeforeUnmount,
  onMounted,
  provide,
  reactive,
  ref,
  useId,
  watch,
  watchEffect,
} from 'vue';
import { nxAnnounce } from '../Announce/announcer';
import { reorderItems } from '../Sortable/sortMath';
import { useNxSortable, useNxSortableMessages } from '../Sortable/useNxSortable';
import NavBarPanel from './NavBarPanel.vue';
import NavBarRootButton from './NavBarRootButton.vue';
import NavBarSideProvider from './NavBarSideProvider.vue';
import { navBarFocusedKey, navBarOpenPathKey, navBarThemeKey } from './internal';
import { escapeClosesPanel, opensPanel } from './panel';
import type { NavBarNode, RegisterFn } from './types';
import { useNavBarKeyboard } from './useNavBarKeyboard';
import { useNavBarOverflow } from './useNavBarOverflow';

const props = withDefaults(
  defineProps<{
    onIconClick?: () => void;
    theme?: 'light' | 'dark';
    /**
     * The user may reorder the left root entries that carry an `itemKey`: drag
     * them (mouse, pen, touch) or Alt+ArrowLeft / Alt+ArrowRight on the
     * focused one. The bar only reports (`reorder`); the host owns the order
     * and renders its `NavBarItem`s in it. Entries without an `itemKey`, the
     * „…“ menu and all right-aligned entries stay put.
     */
    reorderable?: boolean;
  }>(),
  {
    theme: 'dark',
    onIconClick: undefined,
    reorderable: false,
  }
);

const emit = defineEmits<{
  /**
   * `itemKey`s of all left root entries that carry one (also those folded into
   * „…“), in their new order.
   */
  reorder: [keys: string[]];
}>();

provide(navBarThemeKey, props.theme);

// Two parallel registration arrays. They start in mount order and are kept in
// the slot's current order by `syncOrder` below.
const leftItems = ref<NavBarNode[]>([]);
const rightItems = ref<NavBarNode[]>([]);

function makeRegister(target: typeof leftItems): RegisterFn {
  return (node) => {
    target.value.push(node);
    return () => {
      const idx = target.value.indexOf(node);
      if (idx >= 0) target.value.splice(idx, 1);
    };
  };
}
const registerLeft = makeRegister(leftItems);
const registerRight = makeRegister(rightItems);

// Containers used by the overflow composable.
const visibleLeftRef = ref<HTMLElement | null>(null);
const hiddenLeftRef = ref<HTMLElement | null>(null);
const { maxVisibleLeft } = useNavBarOverflow(
  visibleLeftRef,
  hiddenLeftRef,
  computed(() => leftItems.value.map((n) => n.id).join('|'))
);

// ---- slot order ----

// Registration order is mount order, which stays true only until the host moves
// a keyed `v-for` of NavBarItems: Vue moves those components (and their DOM)
// without remounting them, so nothing registers again. Every NavBarItem puts a
// marker into the hidden tree; its document order IS the slot order. Sorting the
// node arrays in place keeps node identity stable (open paths hold references).
const hiddenTreeRef = ref<HTMLElement | null>(null);

function syncOrder() {
  const tree = hiddenTreeRef.value;
  if (!tree) return;
  let moved = false;
  const rank = new Map<string, number>();
  tree.querySelectorAll<HTMLElement>('[data-navbar-order]').forEach((marker, index) => {
    rank.set(marker.dataset.navbarOrder ?? '', index);
  });
  const rankOf = (node: NavBarNode) => rank.get(node.id) ?? Number.MAX_SAFE_INTEGER;
  const sortList = (list: NavBarNode[]) => {
    // `Array.prototype.sort` is stable: unranked nodes keep their relative order.
    const sorted = [...list].sort((a, b) => rankOf(a) - rankOf(b));
    if (sorted.some((node, index) => node !== list[index])) {
      list.splice(0, list.length, ...sorted);
      moved = true;
    }
    // Every branch, also one with a single child: its grandchildren may move.
    for (const node of list) if (node.children.length > 0) sortList(node.children);
  };
  sortList(leftItems.value);
  sortList(rightItems.value);
  if (!moved) return;
  // An open menu or panel would be torn out of place by the move (a native
  // popover closes when its element moves): close it, keeping the state true.
  // Focus inside it (a menu item, a panel's content) goes back to its root
  // entry, the one that stays visible.
  let menuRoot: NavBarNode | null = null;
  if (openPath.value.length > 0) {
    const active = document.activeElement;
    const inPanel = active instanceof Element && !!active.closest('[data-navbar-panel]');
    if (inPanel || focusedNode.value?.parent) menuRoot = openPath.value[0];
    exitMenu();
  }
  void keepFocus(menuRoot);
}

/**
 * Re-rendering moves entries in the DOM, and moving the focused one blurs it —
 * also when the host re-sorted on its own (another tab, a setting). Once the
 * bar has followed (and the overflow has measured again), focus goes back to
 * the same entry — to `menuRoot` when it sat in a menu that was closed — or
 * to „…“ when the entry was folded into it, unless focus went somewhere else
 * meanwhile.
 */
async function keepFocus(menuRoot: NavBarNode | null = null) {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement) || !rootEl.value?.contains(active)) return;
  const id = active.dataset.id;
  await nextTick();
  await nextTick();
  const now = document.activeElement;
  if (now !== null && now !== document.body && now !== active) return;
  if (!menuRoot && active.isConnected) {
    if (now !== active) active.focus();
    return;
  }
  const node = menuRoot ?? [...leftItems.value, ...rightItems.value].find((n) => n.id === id);
  if (!node) return;
  const folded = leftItems.value.indexOf(node) >= maxVisibleLeft.value;
  setFocused(folded && overflowNode.value ? overflowNode.value : node);
}

let orderObserver: MutationObserver | null = null;
onMounted(() => {
  syncOrder();
  if (typeof MutationObserver === 'undefined' || !hiddenTreeRef.value) return;
  orderObserver = new MutationObserver(syncOrder);
  orderObserver.observe(hiddenTreeRef.value, { childList: true, subtree: true });
});
onBeforeUnmount(() => {
  orderObserver?.disconnect();
  orderObserver = null;
});

// Synthetic overflow node. Children = overflowed left items.
const overflowNodeId = `overflow-${useId()}`;
const overflowNode = ref<NavBarNode | null>(null);
/** Left entries with a panel already warned about while in „…“ (DEV only). */
const warnedOverflowPanels = new WeakSet<NavBarNode>();

watchEffect(() => {
  // Reset parent on every left root every run. Without this, items that were
  // overflowed earlier (e.g. during the initial maxVisibleLeft=0 paint) stay
  // permanently parented to overflowNode even after they become visible —
  // which breaks root-key handling (`focused.parent === null` would be false).
  for (const item of leftItems.value) item.parent = null;

  const children = leftItems.value.slice(maxVisibleLeft.value);
  if (children.length === 0) {
    overflowNode.value = null;
    return;
  }
  if (overflowNode.value === null) {
    overflowNode.value = reactive({
      id: overflowNodeId,
      text: '…',
      iconOnly: true,
      // `markRaw` keeps this reactive node from wrapping the icon component.
      icon: markRaw(MoreHorizontalIcon),
      side: 'left' as const,
      parent: null,
      children: [],
    }) as NavBarNode;
  }
  // Mutate in place: identity stays stable so openPath references remain valid.
  overflowNode.value.children = children;
  if (import.meta.env.DEV) {
    for (const c of children) {
      if (!c.panel || warnedOverflowPanels.has(c)) continue;
      warnedOverflowPanels.add(c);
      console.warn(`NavBarItem "${c.text}": in the „…“ menu, \`panel\` is ignored.`);
    }
  }
  // Reparent overflowed nodes so keyboard nav walks back to the synthetic
  // overflow root instead of to NavBar itself.
  for (const c of overflowNode.value.children) c.parent = overflowNode.value;
});

// Keyboard composable.
const {
  focusedNode,
  openPath,
  rootRow,
  setFocused,
  activate,
  hover,
  exitMenu,
  closePanel,
  onRootKey,
  onSubKey,
} = useNavBarKeyboard(leftItems, rightItems, maxVisibleLeft, overflowNode);

// ---- reordering ----

const hasKey = (node: NavBarNode) => !!node.itemKey;
/** Every left root entry with a key, in bar order — the list `reorder` reports. */
const keyedLeft = computed(() => leftItems.value.filter(hasKey));
/** Folded entries sit at the end of the row, so the visible keyed ones are a prefix of `keyedLeft`. */
const visibleKeyedCount = computed(
  () => leftItems.value.slice(0, maxVisibleLeft.value).filter(hasKey).length
);

/** Tell the host the new order, and put focus back on the moved entry once the bar has followed. */
async function commitReorder(from: number, to: number, refocus: NavBarNode | null) {
  if (from === to) return;
  emit(
    'reorder',
    reorderItems(keyedLeft.value, from, to).map((node) => node.itemKey as string)
  );
  if (!refocus) return;
  // Moving a focused element in the DOM blurs it. The host re-renders first,
  // then the bar re-sorts (the observer would too, a tick later).
  await nextTick();
  syncOrder();
  await nextTick();
  setFocused(refocus);
}

// Pointer half only (`pointerProps`): `handleProps` would turn the menu item into
// a grip button (aria-label, role description, own keys). Keys are below.
const sortableMessages = useNxSortableMessages();
const sortable = useNxSortable<NavBarNode>({
  items: keyedLeft,
  container: visibleLeftRef,
  axis: 'horizontal',
  disabled: () => !props.reorderable,
  labelOf: (node) => node.text,
  onReorder: (from, to) => {
    const node = keyedLeft.value[from];
    const active = document.activeElement;
    const focused = active instanceof HTMLElement && active.dataset.id === node?.id;
    void commitReorder(from, to, focused ? node : null);
  },
});

/** Props a visible keyed left root entry needs while the bar is reorderable. */
function reorderOf(node: NavBarNode) {
  if (!props.reorderable || !node.itemKey) return undefined;
  const index = keyedLeft.value.indexOf(node);
  if (index < 0) return undefined;
  return { itemProps: sortable.itemProps(index), pointerProps: sortable.pointerProps(index) };
}

/** Alt+Arrow on a keyed visible root entry. Returns whether the entry owns the key. */
function moveByKeyboard(node: NavBarNode, step: -1 | 1): boolean {
  const from = keyedLeft.value.indexOf(node);
  if (from < 0 || from >= visibleKeyedCount.value) return false;
  const to = from + step;
  // Only within the visible entries: a neighbour folded into „…“ is out of reach.
  if (to < 0 || to >= visibleKeyedCount.value) return true;
  // An open menu or panel would be torn out of place by the move: close it first.
  if (openPath.value.length > 0) exitMenu();
  nxAnnounce(sortableMessages.moved(node.text, to + 1, keyedLeft.value.length));
  void commitReorder(from, to, node);
  return true;
}

provide(navBarOpenPathKey, openPath);
provide(navBarFocusedKey, focusedNode);

// A drag must not leave a popover open under the moving entry.
watch(sortable.active, (active) => {
  if (active) exitMenu();
});

const focusedId = computed(() => focusedNode.value?.id ?? null);
const openIds = computed(() => openPath.value.map((n) => n.id));

// Visible root entries that open a panel. Their panels are rendered next to
// the `role="menubar"` frame, not inside it: a dialog is no allowed child of a
// menubar. They stay inside `rootEl`, so outside click and keys still apply.
const panelNodes = computed(() =>
  [...leftItems.value.slice(0, maxVisibleLeft.value), ...rightItems.value].filter(opensPanel)
);

// Outside-pointer-down closes all popovers.
const rootEl = ref<HTMLElement | null>(null);
function onDocumentPointerDown(e: PointerEvent) {
  if (openPath.value.length === 0) return;
  if (rootEl.value && !rootEl.value.contains(e.target as Node)) exitMenu();
}
onMounted(() => {
  document.addEventListener('pointerdown', onDocumentPointerDown, true);
});
onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onDocumentPointerDown, true);
});

defineSlots<{
  icon?: () => unknown;
  leftAligned?: () => unknown;
  rightAligned?: () => unknown;
}>();

// Initial focus: first left root when items first register, else first right.
// `immediate: true` runs once during setup with empty arrays (no-op); the real
// assignment happens on the first reactive update after NavBarItems mount.
watch(
  [() => leftItems.value.length, () => rightItems.value.length],
  ([leftLen, rightLen]) => {
    if (focusedNode.value !== null) return;
    if (leftLen > 0) focusedNode.value = leftItems.value[0];
    else if (rightLen > 0) focusedNode.value = rightItems.value[0];
  },
  { immediate: true }
);

function rootKeyHandler(e: KeyboardEvent) {
  // Keys typed inside a panel belong to its content; Escape closes it.
  if (e.target instanceof Element && e.target.closest('[data-navbar-panel]')) {
    if (escapeClosesPanel(e)) {
      e.preventDefault();
      closePanel(true);
    }
    return;
  }
  const focused = focusedNode.value;
  if (!focused) return;
  if (
    props.reorderable &&
    focused.parent === null &&
    e.altKey &&
    !e.ctrlKey &&
    !e.metaKey &&
    !e.shiftKey &&
    (e.key === 'ArrowLeft' || e.key === 'ArrowRight') &&
    moveByKeyboard(focused, e.key === 'ArrowLeft' ? -1 : 1)
  ) {
    e.preventDefault();
    return;
  }
  if (focused.parent === null) {
    const row = rootRow.value;
    const entry = row.find((r) => {
      if ('isOverflow' in r) return focused.id === overflowNode.value?.id;
      return r.id === focused.id;
    });
    if (entry) onRootKey(e, entry);
  } else {
    onSubKey(e, focused);
  }
}

function onRootButtonActivate(node: NavBarNode, e: PointerEvent | KeyboardEvent) {
  activate(node, e);
}

function onRootButtonHover(node: NavBarNode) {
  hover(node);
}

function onRootButtonFocus(node: NavBarNode, e: FocusEvent) {
  if ((e.target as HTMLElement | null)?.dataset?.id !== node.id) return;
  setFocused(node);
}
</script>

<template>
  <div ref="rootEl" :class="['menubar-root', theme]" @keydown="rootKeyHandler">
    <div class="menubar" role="menubar">
      <div v-if="$slots.icon" class="logo" @click="() => onIconClick?.()">
        <slot name="icon" />
      </div>

      <!-- Visible left row (overflow-aware). -->
      <div ref="visibleLeftRef" class="menubar-root-align-left" role="none">
        <NavBarRootButton
          v-for="node in leftItems.slice(0, maxVisibleLeft)"
          :key="node.id"
          :node="node"
          :highlighted="focusedId === node.id"
          :open="openIds.includes(node.id)"
          :focused-id="focusedId"
          :open-child-ids="openIds"
          :reorder="reorderOf(node)"
          @activate="(e) => onRootButtonActivate(node, e)"
          @activate-child="(c, e) => onRootButtonActivate(c, e)"
          @hover-child="(c) => onRootButtonHover(c)"
          @pointer-enter="onRootButtonHover(node)"
          @focus.capture="(e: FocusEvent) => onRootButtonFocus(node, e)"
        />
        <NavBarRootButton
          v-if="overflowNode"
          :key="overflowNode.id"
          :node="overflowNode"
          :highlighted="focusedId === overflowNode.id"
          :open="openIds.includes(overflowNode.id)"
          :focused-id="focusedId"
          :open-child-ids="openIds"
          suppress-chevron
          @activate="(e) => onRootButtonActivate(overflowNode!, e)"
          @activate-child="(c, e) => onRootButtonActivate(c, e)"
          @hover-child="(c) => onRootButtonHover(c)"
          @pointer-enter="onRootButtonHover(overflowNode!)"
          @focus.capture="(e: FocusEvent) => onRootButtonFocus(overflowNode!, e)"
        />
      </div>

      <!-- Visible right row. -->
      <div class="menubar-root-align-right" role="none">
        <NavBarRootButton
          v-for="node in rightItems"
          :key="node.id"
          :node="node"
          :highlighted="focusedId === node.id"
          :open="openIds.includes(node.id)"
          :focused-id="focusedId"
          :open-child-ids="openIds"
          @activate="(e) => onRootButtonActivate(node, e)"
          @activate-child="(c, e) => onRootButtonActivate(c, e)"
          @hover-child="(c) => onRootButtonHover(c)"
          @pointer-enter="onRootButtonHover(node)"
          @focus.capture="(e: FocusEvent) => onRootButtonFocus(node, e)"
        />
      </div>
    </div>

    <!-- Panels of root entries: outside the menubar, anchored to their entry. -->
    <NavBarPanel
      v-for="node in panelNodes"
      :key="`panel-${node.id}`"
      :node="node"
      :open="openIds.includes(node.id)"
      @close="closePanel(true)"
    />

    <!-- Hidden registration drivers — render slot content so NavBarItems mount and register. -->
    <div ref="hiddenTreeRef" class="navbar-hidden-tree" aria-hidden="true">
      <NavBarSideProvider :side="'left'" :register="registerLeft">
        <slot name="leftAligned" />
      </NavBarSideProvider>
      <NavBarSideProvider :side="'right'" :register="registerRight">
        <slot name="rightAligned" />
      </NavBarSideProvider>
    </div>
  </div>

  <!-- Hidden measurement ghost row for left items + overflow trigger. -->
  <div class="menubar-root menubar-root--ghost" aria-hidden="true">
    <div ref="hiddenLeftRef" class="menubar-root-align-left">
      <NavBarRootButton
        v-for="node in leftItems"
        :key="`ghost-${node.id}`"
        :node="node"
        :highlighted="false"
        :open="false"
        :focused-id="null"
        :open-child-ids="[]"
        ghost
      />
      <!-- Ghost overflow trigger always present so we can measure its width. -->
      <NavBarRootButton
        :node="{
          id: '__ghost_overflow__',
          text: '…',
          iconOnly: true,
          icon: MoreHorizontalIcon,
          side: 'left',
          parent: null,
          children: [],
        }"
        :highlighted="false"
        :open="false"
        suppress-chevron
        :focused-id="null"
        :open-child-ids="[]"
        ghost
      />
    </div>
  </div>
</template>

<style scoped>
.menubar-root {
  display: flex;
  padding: var(--nxt-space-2xs);
  box-sizing: border-box;
  box-shadow: var(--nxt-shadow-md);
  min-height: calc(var(--nxt-size-unit) * 3.375);
  gap: var(--nxt-space-2xs);
  width: 100%;
  z-index: var(--nxt-z-sticky);
  overflow: hidden;
}

.menubar {
  /* The bar's row: takes the root's width and lays the entries out as the
     root did before the panels moved next to it. */
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  gap: var(--nxt-space-2xs);
}

.menubar-root.light {
  background-color: var(--nxt-surface-bg);
}
.menubar-root.dark {
  background-color: var(--nxt-navbar-bg);
  border-bottom: var(--nxt-hairline) solid var(--nxt-border);
}

.menubar-root--ghost {
  width: fit-content;
  position: fixed;
  left: 0;
  top: calc(-1 * var(--nxt-space-md) * 6.25);
  visibility: hidden;
}

.logo {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 0 var(--nxt-space-xs);
  cursor: pointer;
}
.logo :deep(svg) {
  height: var(--nxt-icon-size-xl);
  padding: 0;
}

.menubar-root-align-left {
  flex-grow: 1;
  /* A flex item never shrinks below its content (`min-width: auto`), so without
     this the row keeps the width of all its items, the overflow measurement sees
     room for every one of them and the root's `overflow: hidden` clips the right
     side instead of the „…“ trigger taking over. Only shows with many items. */
  min-width: 0;
  display: flex;
  gap: var(--nxt-space-2xs);
}
.menubar-root-align-right {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  gap: var(--nxt-space-2xs);
}

.navbar-hidden-tree {
  display: none;
}

/* `data-highlighted` paints only while the navbar holds DOM focus. Without
   this gate the last-focused item would stay visually selected after the
   user tabs away. */
.menubar-root:focus-within :deep(.menubar-trigger[data-highlighted].light) {
  background-color: var(--nxt-brand-subtle-bg);
}
.menubar-root:focus-within :deep(.menubar-trigger[data-highlighted].dark) {
  background-color: var(--nxt-navbar-item-hover-bg);
}
.menubar-root:focus-within :deep(.menubar-item[data-highlighted]) {
  background-color: var(--nxt-brand-subtle-bg);
}
</style>
